import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  flatMap,
  map,
  publishReplay,
  refCount,
  shareReplay
} from 'rxjs/operators';

import { DashboardSettingsDto } from '../shared/components/modal/dashboard-goal-settings-modal/interfaces/dashboard-settings.dto';
import { DashboardSettings } from '../shared/components/modal/dashboard-goal-settings-modal/models/dashboard-settings';
import { RpcService } from '../shared/http/rpc.service';
import { UserDataDto } from './interfaces/user-data.interface';
import { UserData } from './models/user-data';
import { TokenService } from '../authentication/token.service';
import {
  CapitalOffer,
  CapitalOfferDTO
} from './main-dashboard-ls-capital/main-dashboard-ls-capital.types';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class DashboardService {
  public dashboardSettings$: Observable<DashboardSettings>;
  public dashboardSettingChanges$: Observable<DashboardSettings>;
  private dashboardSettingsSubject: Subject<DashboardSettings> = new Subject<
    DashboardSettings
  >();
  private dashboardSettingChangesSubject: Subject<
    DashboardSettings
  > = new Subject<DashboardSettings>();

  private dashboardGoalSettings$: Observable<DashboardSettings>;
  private readonly filterKey: string = 'dashboard_goal_settings';

  constructor(
    private rpcService: RpcService,
    private tokenService: TokenService,
    private http: HttpClient
  ) {
    this.dashboardSettings$ = this.dashboardSettingsSubject.asObservable();
    this.dashboardSettingChanges$ = this.dashboardSettingChangesSubject.asObservable();

    this.tokenService.tokenChanged$.subscribe(() => {
      this.dashboardGoalSettings$ = null;
    });
  }

  public getDashboardSettings(forceReload: boolean = false): void {
    if (!this.dashboardGoalSettings$ || forceReload) {
      this.dashboardGoalSettings$ = this.getUserData().pipe(
        map((userData: Array<UserDataDto>) => {
          if (userData.length === 0) {
            return DashboardSettings.getDefaultDashboardSettings();
          }

          const settingsField: UserData = userData
            .map(UserData.fromDto)
            .find((data: UserData) => {
              return data.key === this.filterKey;
            });

          if (!settingsField) {
            // Get average values
            return DashboardSettings.getDefaultDashboardSettings();
          }

          return DashboardSettings.fromDto(JSON.parse(settingsField.value));
        }),
        publishReplay(1),
        refCount()
      );
    }
    this.dashboardGoalSettings$.subscribe(
      (dashboardSettings: DashboardSettings) =>
        this.dashboardSettingsSubject.next(dashboardSettings)
    );
  }

  public saveSettings(settings: DashboardSettings): void {
    this.getUserData()
      .pipe(
        flatMap((result: Array<UserData>) => {
          const userData: Array<UserData> = [
            this.insertUpdateDashboardGoalSettings(
              result,
              DashboardSettings.toDto(settings)
            )
          ];
          return this.rpcService.makeManagerCall('setUserData', userData);
        })
      )
      .subscribe(() => {
        this.dashboardSettingChangesSubject.next(settings);
        this.getDashboardSettings(true);
      });
  }

  private getUserData(): Observable<Array<UserData>> {
    return this.rpcService.makeManagerCall('getUserData');
  }

  private insertUpdateDashboardGoalSettings(
    userData: Array<UserData>,
    settings: DashboardSettingsDto
  ): any {
    let updatedSettings = false;

    for (let i = 0; i < userData.length; i++) {
      if (userData[i].key === this.filterKey) {
        userData[i].value = JSON.stringify(settings);
        updatedSettings = true;
      }
    }

    if (!updatedSettings) {
      userData.push({
        key: this.filterKey,
        value: JSON.stringify(settings)
      });
    }

    return userData;
  }

  public getOffer(): Observable<CapitalOffer> {
    return this.http
      .get<CapitalOfferDTO>(
        '/PosServer/rest/manager/v1/lightspeed-capital/offer'
      )
      .pipe(
        map(response => {
          if (response.offerDTO) {
            return {
              hideOfferBanner: response.hideOfferBanner,
              offerDTO: {
                currency: response.offerDTO.currency,
                offerAmount: response.offerDTO.maxAmount / 100
              }
            };
          }
          return {
            hideOfferBanner: response.hideOfferBanner
          };
        }),
        shareReplay(1)
      );
  }

  public hideOfferBanner(): void {
    this.http
      .post<{ data: string }>(
        '/PosServer/rest/manager/v1/lightspeed-capital/hide-offer-banner',
        {}
      )
      .subscribe();
  }
}
